<script>
import SpellCheckedText from "@/components/SpellCheckedText.vue";
import PhotoBox from "@/components/PhotoBox.vue";

export default {
    name: "TOCTemp3",
    components: {
        SpellCheckedText,
        PhotoBox
    },
    props: {
        toc: { type: Object, required: true },
        pageGap: { type: Boolean, default: true },
    },
    data() {
        return {
            pages: [],
            overlayImage: ""
        };
    },
    computed: {
        words() {
            return ((this.toc && this.toc.title) || "").trim().split(" ");
        },
        firstLine() {
            return this.words.length > 1
                ? this.words.slice(0, -1).join(" ")
                : this.words[0] || "";
        },
        lastWord() {
            return this.words.length > 1
                ? this.words[this.words.length - 1]
                : "";
        },
        headerColor() {
            return this.toc && this.toc.style && this.toc.style.colorScheme && this.toc.style.colorScheme.header;
        }
    },
    watch: {
        toc: {
            deep: true,
            handler() {
                this.$nextTick(() => {
                    this.splitContentIntoPages();
                    this.generateOverlayImage();
                });
            }
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.splitContentIntoPages();
            this.generateOverlayImage();
        });
    },
    methods: {
        splitContentIntoPages() {
            if (!this.toc || !this.toc.pages) return;

            const itemsPerPage = parseInt(this.toc.itemsPerPage);
            const projects = this.toc.projects || [];
            let allTocItems = [...this.toc.pages].map((item, index) => ({
                ...item,
                alphabet: this.getAlphabet(index)
            }));

            this.pages = [];
            let pagesProjectsIndex = 0;

            while (allTocItems.length > 0) {
                let currentPageItems = allTocItems.splice(0, itemsPerPage);

                this.pages.push({
                    items: currentPageItems,
                    project: projects[pagesProjectsIndex] || null
                });
                pagesProjectsIndex++;

                if (this.pages.length > 50) break;
            }

            if (this.pages.length === 0) {
                this.pages.push({ items: [], project: projects[0] || null });
            }
        },
        updateFirstLine(value) {
            this.toc.title = value + " " + this.lastWord;
        },
        updateLastWord(value) {
            this.toc.title = this.firstLine + " " + value;
        },
        getAlphabet(index) {
            let result = "";
            index += 1;

            while (index > 0) {
                let rem = (index - 1) % 26;
                result = String.fromCharCode(97 + rem) + result;
                index = Math.floor((index - 1) / 26);
            }

            return result.toUpperCase();
        },
        hexToRGBA(hex, opacity) {
            if (!hex) return `rgba(0,0,0,${opacity})`;
            let r = 0, g = 0, b = 0;
            const cleanHex = hex.replace('#', '');
            if (cleanHex.length === 3) {
                r = parseInt(cleanHex[0] + cleanHex[0], 16);
                g = parseInt(cleanHex[1] + cleanHex[1], 16);
                b = parseInt(cleanHex[2] + cleanHex[2], 16);
            } else if (cleanHex.length === 6) {
                r = parseInt(cleanHex.substring(0, 2), 16);
                g = parseInt(cleanHex.substring(2, 4), 16);
                b = parseInt(cleanHex.substring(4, 6), 16);
            }
            return `rgba(${r},${g},${b},${opacity})`;
        },
        generateOverlayImage() {
            const canvas = document.createElement('canvas');
            canvas.width = 800;
            canvas.height = 1200;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = this.hexToRGBA(this.headerColor, 0.85);
            ctx.fillRect(0, 0, 800, 1200);
            this.overlayImage = canvas.toDataURL();
        }
    }
};
</script>

<template>
    <div class="main-container" :style="{
        gap: pageGap ? '20px' : '0px',
        '--header-color': toc.style.colorScheme && toc.style.colorScheme.header,
        '--body-color': toc.style.colorScheme && toc.style.colorScheme.body,
        '--footer-color': toc.style.colorScheme && toc.style.colorScheme.footer,
        '--heading-color': toc.style.heading.color,
        '--heading-size': toc.style.heading.size + 'px',
        '--heading-weight': toc.style.heading.bold ? 'bold' : 'normal',
        '--heading-style': toc.style.heading.italic ? 'italic' : 'normal',
        '--heading-decoration': toc.style.heading.underline ? 'underline' : 'none',
        '--heading-font': toc.style.heading.font,

        '--label-color': toc.style.label.color,
        '--label-size': toc.style.label.size + 'px',
        '--label-weight': toc.style.label.bold ? 'bold' : 'normal',
        '--label-style': toc.style.label.italic ? 'italic' : 'normal',
        '--label-decoration': toc.style.label.underline ? 'underline' : 'none',
        '--label-font': toc.style.label.font,

        '--text-color': toc.style.text.color || '#000000',
        '--text-size': toc.style.text.size + 'px',
        '--text-weight': toc.style.text.bold ? 'bold' : 'normal',
        '--text-style': toc.style.text.italic ? 'italic' : 'normal',
        '--text-decoration': toc.style.text.underline ? 'underline' : 'none',
        '--text-font': toc.style.text.font,
    }">
        <div class="sub-container" v-for="(page, pageIndex) in pages" :key="pageIndex">
            <!-- Background Image -->
            <div class="bg-layer photobox">
                <PhotoBox v-if="page.project" :project="page.project" :bgColor="toc.style.colorScheme && toc.style.colorScheme.body"
                    position="top-center" />
                <div v-else class="bg-placeholder" :style="{ background: (toc.style.colorScheme && toc.style.colorScheme.body) || '#eee' }"></div>

                <div class="full-page-overlay" v-if="overlayImage">
                    <img :src="overlayImage" class="w-full h-full" />
                </div>
            </div>

            <div class="content">
                <div class="content-wrapper">

                    <div class="toc-header" v-if="pageIndex === 0">
                        <h1 class="titles firstLine">
                            <SpellCheckedText :value="firstLine || ''" @input="updateFirstLine" />
                        </h1>
                        <h1 class="titles lastWord">
                            <SpellCheckedText :value="lastWord || ''" @input="updateLastWord" />
                        </h1>
                    </div>

                    <div class="toc-list">
                        <div v-for="(item, idx) in page.items" :key="idx" class="toc-item">
                            <span class="alphabet">Section {{ item.alphabet }}.</span>
                            <span class="heading">
                                <SpellCheckedText :value="item.heading || ''" @input="item.heading = $event" />
                            </span>
                            <div class="dots"></div>
                            <span class="page-num">{{ String(item.page || '').padStart(2, '0') }}</span>
                        </div>
                    </div>
                </div>

                <footer class="toc-footer">
                    <div class="footer-line"></div>
                    <div class="project-info">
                        <SpellCheckedText :value="toc.opprName || ''" @input="toc.opprName = $event" />
                    </div>
                </footer>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.main-container {
    display: flex;
    flex-direction: column;
    width: 211mm;
    min-height: 296.83mm;
    position: relative;
    box-sizing: border-box;

    &:hover {
        .photobox :deep(.photo-overlay) {
            opacity: 1;
        }

        .photobox :deep(img) {
            opacity: 0.9;
        }
    }

    .sub-container {
        position: relative;
        height: 296.83mm;
        overflow: hidden;

        .bg-layer {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: 1;

            .bg-placeholder {
                width: 100%;
                height: 100%;
            }

            .full-page-overlay {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                z-index: 2;
                pointer-events: none;
            }
        }

        .content {
            position: relative;
            z-index: 2;
            height: 100%;
            display: flex;
            flex-direction: column;
            box-sizing: border-box;
            pointer-events: none;

            .content-wrapper {
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                height: 100%;
                padding: 50px 50px 0px 50px;

                .toc-header {
                    text-align: center;
                    pointer-events: auto;

                    .titles {
                        color: var(--heading-color);
                        font-family: var(--heading-font);
                        font-weight: var(--heading-weight);
                        font-style: var(--heading-style);
                        text-decoration: var(--heading-decoration);
                        margin: 0;
                        text-transform: uppercase;
                        letter-spacing: 5px;
                    }

                    .firstLine {
                        font-size: var(--heading-size);
                    }

                    .lastWord {
                        font-size: calc(var(--heading-size) * 2);
                    }
                }

                .toc-list {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                    margin: 0 auto;
                    width: 100%;
                    min-height: 400px;
                    padding: 40px 80px;
                    pointer-events: auto;

                    .toc-item {
                        display: flex;
                        align-items: flex-start;

                        .alphabet {
                            font-size: var(--label-size);
                            color: var(--label-color);
                            font-family: var(--label-font);
                            font-weight: var(--label-weight);
                            font-style: var(--label-style);
                            text-decoration: var(--label-decoration);
                            min-width: 100px;
                        }

                        .heading {
                            font-size: var(--text-size);
                            color: var(--text-color);
                            font-family: var(--text-font);
                            font-weight: var(--text-weight);
                            font-style: var(--text-style);
                            text-decoration: var(--text-decoration);
                            min-width: 0;
                        }

                        .dots {
                            flex: 1;
                            border-bottom: 1px dotted var(--text-color);
                            margin: 16px 4px 0 4px;
                        }

                        .page-num {
                            font-size: var(--text-size);
                            color: var(--text-color);
                            font-family: var(--text-font);
                            font-weight: var(--text-weight);
                            font-style: var(--text-style);
                            text-decoration: var(--text-decoration);
                            min-width: 25px;
                            text-align: right;
                        }
                    }
                }
            }

            .toc-footer {
                pointer-events: auto;
                margin-top: auto;
                padding: 0px 80px 50px 80px;

                .footer-line {
                    width: 100%;
                    height: 2.5px;
                    background: var(--footer-color);
                    margin-bottom: 10px;
                }

                .project-info {
                    font-size: 16px;
                    color: var(--footer-color);
                }
            }
        }
    }
}
</style>
